import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Clock, ListVideo, Image, Monitor } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { Schedule, CreateScheduleInput } from '@/hooks/signage';

interface Playlist {
  id: string;
  name: string;
}

interface ContentItem {
  id: string;
  name: string;
  content_type: string;
}

interface Display {
  id: string;
  name: string;
}

interface CreateScheduleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  businessId: string;
  schedule?: Schedule | null; // For editing
  onSubmit: (data: CreateScheduleInput) => Promise<void>;
}

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const TIME_OPTIONS = Array.from({ length: 48 }, (_, i) => {
  const hours = Math.floor(i / 2);
  const minutes = (i % 2) * 30;
  const time = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`;
  const h12 = hours % 12 || 12;
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const label = `${h12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  return { value: time, label };
});

export function CreateScheduleDialog({
  open,
  onOpenChange,
  businessId,
  schedule,
  onSubmit,
}: CreateScheduleDialogProps) {
  const [name, setName] = useState('');
  const [contentType, setContentType] = useState<'playlist' | 'content'>('playlist');
  const [playlistId, setPlaylistId] = useState<string | null>(null);
  const [contentItemId, setContentItemId] = useState<string | null>(null);
  const [displayId, setDisplayId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState('09:00:00');
  const [endTime, setEndTime] = useState('17:00:00');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [priority, setPriority] = useState(0);

  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [displays, setDisplays] = useState<Display[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isEditing = !!schedule;

  // Load initial data
  useEffect(() => {
    if (open) {
      loadData();
      if (schedule) {
        // Populate form with schedule data
        setName(schedule.name);
        setPlaylistId(schedule.playlist_id);
        setContentItemId(schedule.content_item_id);
        setDisplayId(schedule.display_id);
        setStartTime(schedule.start_time);
        setEndTime(schedule.end_time);
        setDaysOfWeek(schedule.days_of_week);
        setStartDate(schedule.start_date || '');
        setEndDate(schedule.end_date || '');
        setPriority(schedule.priority);
        setContentType(schedule.playlist_id ? 'playlist' : 'content');
      } else {
        resetForm();
      }
    }
  }, [open, schedule]);

  const loadData = async () => {
    setLoadingData(true);
    try {
      const [playlistsRes, contentRes, displaysRes] = await Promise.all([
        supabase
          .from('signage_playlists')
          .select('id, name')
          .eq('business_id', businessId)
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('signage_content_items')
          .select('id, name, content_type')
          .eq('business_id', businessId)
          .eq('is_active', true)
          .order('name'),
        supabase
          .from('displays')
          .select('id, name')
          .eq('business_id', businessId)
          .order('name'),
      ]);

      setPlaylists(playlistsRes.data || []);
      setContentItems(contentRes.data || []);
      setDisplays(displaysRes.data || []);
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load options');
    } finally {
      setLoadingData(false);
    }
  };

  const resetForm = () => {
    setName('');
    setContentType('playlist');
    setPlaylistId(null);
    setContentItemId(null);
    setDisplayId(null);
    setStartTime('09:00:00');
    setEndTime('17:00:00');
    setDaysOfWeek([0, 1, 2, 3, 4, 5, 6]);
    setStartDate('');
    setEndDate('');
    setPriority(0);
  };

  const handleDayToggle = (day: number) => {
    if (daysOfWeek.includes(day)) {
      setDaysOfWeek(daysOfWeek.filter(d => d !== day));
    } else {
      setDaysOfWeek([...daysOfWeek, day].sort());
    }
  };

  const handleSelectAllDays = () => {
    if (daysOfWeek.length === 7) {
      setDaysOfWeek([]);
    } else {
      setDaysOfWeek([0, 1, 2, 3, 4, 5, 6]);
    }
  };

  const handleSelectWeekdays = () => {
    setDaysOfWeek([1, 2, 3, 4, 5]);
  };

  const handleSelectWeekends = () => {
    setDaysOfWeek([0, 6]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      toast.error('Please enter a schedule name');
      return;
    }

    if (daysOfWeek.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    if (contentType === 'playlist' && !playlistId) {
      toast.error('Please select a playlist');
      return;
    }

    if (contentType === 'content' && !contentItemId) {
      toast.error('Please select a content item');
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit({
        name: name.trim(),
        display_id: displayId,
        playlist_id: contentType === 'playlist' ? playlistId : null,
        content_item_id: contentType === 'content' ? contentItemId : null,
        start_time: startTime,
        end_time: endTime,
        days_of_week: daysOfWeek,
        start_date: startDate || null,
        end_date: endDate || null,
        priority,
      });
      onOpenChange(false);
    } catch (err) {
      // Error handled by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            {isEditing ? 'Edit Schedule' : 'Create Schedule'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Update when this content should be displayed'
              : 'Set up time-based content display rules'}
          </DialogDescription>
        </DialogHeader>

        {loadingData ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 overflow-hidden flex flex-col">
            <ScrollArea className="flex-1 pr-4">
              <div className="space-y-6 py-4">
                {/* Schedule Name */}
                <div className="space-y-2">
                  <Label htmlFor="name">Schedule Name *</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Lunch Menu, Morning Promotions"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                {/* Content Selection */}
                <div className="space-y-3">
                  <Label>Content to Display *</Label>
                  <Tabs value={contentType} onValueChange={(v) => setContentType(v as any)}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="playlist">
                        <ListVideo className="w-4 h-4 mr-2" />
                        Playlist
                      </TabsTrigger>
                      <TabsTrigger value="content">
                        <Image className="w-4 h-4 mr-2" />
                        Single Item
                      </TabsTrigger>
                    </TabsList>
                    <TabsContent value="playlist" className="mt-3">
                      <Select
                        value={playlistId || ''}
                        onValueChange={(v) => setPlaylistId(v || null)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a playlist" />
                        </SelectTrigger>
                        <SelectContent>
                          {playlists.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {p.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TabsContent>
                    <TabsContent value="content" className="mt-3">
                      <Select
                        value={contentItemId || ''}
                        onValueChange={(v) => setContentItemId(v || null)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select content" />
                        </SelectTrigger>
                        <SelectContent>
                          {contentItems.map((c) => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.name} ({c.content_type})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TabsContent>
                  </Tabs>
                </div>

                {/* Time Range */}
                <div className="space-y-3">
                  <Label>Time Range *</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Start Time</Label>
                      <Select value={startTime} onValueChange={setStartTime}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">End Time</Label>
                      <Select value={endTime} onValueChange={setEndTime}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TIME_OPTIONS.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Days of Week */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Days of Week *</Label>
                    <div className="flex gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={handleSelectAllDays}
                      >
                        All
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={handleSelectWeekdays}
                      >
                        Weekdays
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={handleSelectWeekends}
                      >
                        Weekends
                      </Button>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {DAYS_OF_WEEK.map((day) => (
                      <Button
                        key={day.value}
                        type="button"
                        variant={daysOfWeek.includes(day.value) ? 'default' : 'outline'}
                        size="sm"
                        className="flex-1"
                        onClick={() => handleDayToggle(day.value)}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Target Display */}
                <div className="space-y-2">
                  <Label>Target Display</Label>
                  <Select
                    value={displayId || 'all'}
                    onValueChange={(v) => setDisplayId(v === 'all' ? null : v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">
                        <div className="flex items-center gap-2">
                          <Monitor className="w-4 h-4" />
                          All Displays
                        </div>
                      </SelectItem>
                      {displays.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Leave as "All Displays" to apply this schedule to every display
                  </p>
                </div>

                {/* Date Range (Optional) */}
                <div className="space-y-3">
                  <Label>Date Range (Optional)</Label>
                  <p className="text-xs text-muted-foreground">
                    Limit this schedule to a specific date range
                  </p>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Start Date</Label>
                      <Input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">End Date</Label>
                      <Input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Priority */}
                <div className="space-y-2">
                  <Label htmlFor="priority">Priority</Label>
                  <Input
                    id="priority"
                    type="number"
                    min={0}
                    max={100}
                    value={priority}
                    onChange={(e) => setPriority(parseInt(e.target.value) || 0)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Higher priority schedules override lower ones when times overlap
                  </p>
                </div>
              </div>
            </ScrollArea>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEditing ? 'Save Changes' : 'Create Schedule'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
